import React, { useEffect, useMemo, useRef, useState, VFC } from 'react';
import { SearchStrongIcon } from 'src/monet/icons';
import debounce from 'lodash/debounce';
import { Nullable } from 'src/types/nullable.type';
import { IconButton } from 'src/common/icon-button';
import { FilterBar, FilterSpec } from 'src/common/components/filter-bar';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetAllOrganizationTags } from 'src/utils/journeyApi';
import { useCurrentTags } from 'src/store/tags';
import { Tag } from 'src/common/interfaces/tag.interface';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

interface DashboardHomeSearchProps {
  searchInputValue?: string;
  onSearchInputChange: (value: string) => void;
  onTagsChange?: (tags: string) => void;
}

export const DashboardHomeSearch: VFC<DashboardHomeSearchProps> = ({
  searchInputValue,
  onSearchInputChange,
  onTagsChange,
}) => {
  const searchInputRef = useRef<Nullable<HTMLInputElement>>(null);
  const {
    filterBarParams: { filters },
  } = useFilterBarParams();

  const prevTagIds = useRef<string[] | null>(null);
  const { features, showUpgradeModal } = useFeatureRestrictions();

  useEffect(() => {
    const currentTagIds = filters?.tag_ids || [];

    if (
      prevTagIds.current !== null &&
      JSON.stringify(prevTagIds.current) !== JSON.stringify(currentTagIds)
    ) {
      if (!features.canAccessTagsAndFilters) {
        showUpgradeModal('tags_and_filters', 'Upgrade to Grow or Scale plan to filter Journeys by tags.');
      } else if (onTagsChange) {
        onTagsChange(currentTagIds.join(',') || '');
      }
    }

    prevTagIds.current = currentTagIds;
  }, [filters?.tag_ids, features.canAccessTagsAndFilters, showUpgradeModal, onTagsChange]);

  const { currentOrganization } = useCurrentOrganization();
  const { currentTags, setCurrentTags } = useCurrentTags();

  const [showSearchInput, setShowSearchInput] = useState(!!searchInputValue);
  const [filterSpecList, setFilterSpecList] = useState<FilterSpec[]>([]);

  const fetchTags = async () => {
    const tagsResponse = await apiGetAllOrganizationTags(currentOrganization.id);
    setCurrentTags(tagsResponse.tags);
  };

  useEffect(() => {
    const formattedTags = currentTags.map((item: Tag) => ({
      value: String(item.id),
      label: item.name,
    }));
    setFilterSpecList((prev) => {
      const updatedList = prev.filter((spec) => spec.id !== 'tag_ids');
      return [
        ...updatedList,
        {
          id: 'tag_ids',
          label: 'Tags',
          type: 'multi-select',
          searchable: true,
          searchPlaceholder: 'Search for filter',
          options: formattedTags,
        },
      ];
    });
  }, [currentTags]);

  useEffect(() => {
    fetchTags();
  }, []);

  const onSearchIconClicked = () => {
    setShowSearchInput(true);
  };

  const onSearchInputChanged = useMemo(() => debounce(onSearchInputChange, 400), [onSearchInputChange]);

  useEffect(() => {
    const { current } = searchInputRef;
    const onInputBlurred = () => {
      if (current && current.value === '') {
        setShowSearchInput(false);
      }
    };

    if (showSearchInput) {
      current?.focus();
    }

    current?.addEventListener('blur', onInputBlurred);

    return () => {
      current?.removeEventListener('blur', onInputBlurred);
      onSearchInputChanged.cancel();
    };
  }, [showSearchInput, onSearchInputChanged]);

  return (
    <>
      <div className='flex items-center text-bedrock-black'>
        {!showSearchInput ? (
          <IconButton onClick={onSearchIconClicked}>
            <SearchStrongIcon onClick={onSearchIconClicked} />
          </IconButton>
        ) : (
          <input
            ref={searchInputRef}
            type='text'
            defaultValue={searchInputValue}
            onChange={(event) => {
              onSearchInputChanged(event.target.value);
            }}
            className='focus:outline-none focus:ring-0 border-0 px-0.5 text-bedrock-p-strong min-w-[137px] h-[22px] placeholder:text-bedrock-p-strong placeholder:text-bedrock-dark-gray'
            placeholder='Search Journeys'
          />
        )}
      </div>
      <div className='w-px self-stretch rounded-md bg-bedrock-gray-medium' />
      <FilterBar filterSpecList={filterSpecList} hasSearch={false} />
    </>
  );
};
